import { VENDOR_OFFER_SUCCESS_PAGES } from '../pages/vendorOfferSuccessConfig';

/** Keep `public/index.html` default title & meta in sync with `SEO_HOME`. */
export const SITE_ORIGIN = 'https://www.healmusicapp.com';

export type PageSeo = { title: string; description: string };

export const SEO_HOME: PageSeo = {
  title: 'Heal — Music therapy & breathwork for stress, sleep & HRV',
  description:
    'Heal helps you regulate your nervous system and stimulate the vagus nerve. Improve HRV in real time to reduce stress, lower cortisol, and improve sleep.',
};

const BY_PATH: Record<string, PageSeo> = {
  '/': SEO_HOME,
  '/share': {
    title: 'Open Heal from a shared link | Heal',
    description:
      'Open the Heal app from a shared playlist or session link. Music therapy and breathwork personalised to your nervous system.',
  },
  '/terms': {
    title: 'Terms of Service | Heal',
    description:
      'Terms of Service for the Heal app and website from Heal Music Ltd, including subscriptions, health disclaimers, and acceptable use.',
  },
  '/privacy': {
    title: 'Privacy Policy (Website) | Heal',
    description:
      'How Heal Music Ltd collects and uses data when you visit healmusicapp.com and join the waitlist.',
  },
  '/app-privacy': {
    title: 'Privacy Policy | Heal',
    description:
      'Privacy Policy for Heal: data we collect, wearables and HealthKit, OAuth connections, your rights, and how to contact us.',
  },
  '/troubleshooting': {
    title: 'Troubleshooting | Heal',
    description:
      'Fix common Heal app issues: crashes, wearables, HRV sync, music playback, notifications, login, and subscriptions.',
  },
};

const VENDOR_OFFER: PageSeo = {
  title: 'Partner offer | Heal',
  description:
    'Claim your Heal partner or vendor offer and link it to your Heal account.',
};

const OFFER_SUCCESS_TITLE = "You're all set | Heal";

function offerSuccessSeo(pathname: string): PageSeo | null {
  const match = pathname.match(/^\/vendor-offer\/([^/]+)\/success$/);
  if (!match) return null;
  const offer = VENDOR_OFFER_SUCCESS_PAGES[match[1]];
  if (!offer) return null;
  return { title: OFFER_SUCCESS_TITLE, description: offer.body };
}

const NOT_FOUND: PageSeo = {
  title: 'Page not found | Heal',
  description:
    'That page does not exist. Visit Heal for music therapy, breathwork, and HRV-based wellness.',
};

export function normalizePathname(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/, '') || '/';
}

export function getPageSeo(pathname: string): PageSeo {
  const path = normalizePathname(pathname);
  const offerSuccess = offerSuccessSeo(path);
  if (offerSuccess) return offerSuccess;
  if (path.startsWith('/vendor-offer/')) return VENDOR_OFFER;
  return BY_PATH[path] ?? NOT_FOUND;
}

export function canonicalUrlForPath(pathname: string): string {
  const normalizedPath = normalizePathname(pathname);
  const suffix = normalizedPath === '/' ? '/' : normalizedPath;
  return `${SITE_ORIGIN}${suffix}`;
}

const KNOWN_PATHS = new Set(['/', '/share', '/terms', '/privacy', '/app-privacy', '/troubleshooting']);

/** True for URLs that are not real marketing/product pages (e.g. typos). */
export function shouldNoIndexPath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  if (/^\/vendor-offer\/[^/]+\/success$/.test(path)) return true;
  if (path.startsWith('/vendor-offer/')) return false;
  return !KNOWN_PATHS.has(path);
}
